import { useState } from 'react'
import { History } from 'lucide-react'

import { formatRelativeDateTime } from '@/lib/relativeDateTime'
import type { AddendumChange } from '@/lib/schemas/db'

/** Shape shared by CustomerRevision and ServiceRevision. */
export interface RevisionLike {
  id: string
  authorId: string
  createdAt: string
  changes: AddendumChange[]
}

export interface RevisionsCardProps {
  /** Oldest first. */
  revisions: RevisionLike[]
  getAuthorDisplayName: (authorId: string) => string
}

function ChangeRow(props: { change: AddendumChange }) {
  const { change } = props
  return (
    <li>
      <span className="text-slate-500">{change.label}:</span>{' '}
      <span className="text-rose-700/80 line-through decoration-rose-400">
        {change.before === '' ? '(empty)' : change.before}
      </span>{' '}
      <span className="text-slate-400">→</span>{' '}
      <span className="font-medium text-emerald-800">
        {change.after === '' ? '(cleared)' : change.after}
      </span>
    </li>
  )
}

/**
 * The add-only edit trail behind a live record's details — customers and open
 * services alike.
 *
 * The browse-list half of {@link AddendumsCard} without the amend half: the
 * record is not locked, so its fields stay directly editable and each save
 * lands here as an immutable revision (appended server-side; no path can skip
 * it). Quiet by default: a count and a toggle, the full trail one click away.
 */
export function RevisionsCard(props: RevisionsCardProps) {
  const { revisions, getAuthorDisplayName } = props

  const [browseOpen, setBrowseOpen] = useState(false)
  const [openRevisionId, setOpenRevisionId] = useState<string | null>(null)

  if (revisions.length === 0) return null

  const latest = revisions[revisions.length - 1]

  return (
    <section className="rounded-md border border-slate-200 bg-white p-4 space-y-2">
      <div className="flex items-center gap-2">
        <History className="h-3.5 w-3.5 text-slate-500" aria-hidden="true" />
        <button
          type="button"
          className="text-xs font-medium text-slate-700 hover:text-slate-900"
          onClick={() => setBrowseOpen((open) => !open)}
        >
          {revisions.length} change{revisions.length === 1 ? '' : 's'} to these
          details — latest by {getAuthorDisplayName(latest.authorId)} ·{' '}
          {formatRelativeDateTime(latest.createdAt)} {browseOpen ? '▴' : '▾'}
        </button>
      </div>

      {browseOpen ? (
        <ol className="divide-y divide-slate-100 rounded-md border border-slate-200">
          {[...revisions].reverse().map((revision) => {
            const number = revisions.indexOf(revision) + 1
            const open = openRevisionId === revision.id
            return (
              <li key={revision.id}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-slate-50"
                  onClick={() => setOpenRevisionId(open ? null : revision.id)}
                >
                  <span className="text-xs font-medium text-slate-800">
                    R{number} · {getAuthorDisplayName(revision.authorId)}
                  </span>
                  <span className="text-[11px] text-slate-500">
                    {formatRelativeDateTime(revision.createdAt)}{' '}
                    {open ? '▴' : '▾'}
                  </span>
                </button>
                {open ? (
                  <div className="border-t border-slate-100 bg-slate-50 px-3 py-2">
                    <ul className="space-y-1 text-[11px]">
                      {revision.changes.map((change) => (
                        <ChangeRow key={change.field} change={change} />
                      ))}
                    </ul>
                  </div>
                ) : null}
              </li>
            )
          })}
        </ol>
      ) : null}
    </section>
  )
}
